// src/services/axios.instance.ts
// Axios instance configured per BEST_PRACTICES.md specifications:
// - withCredentials: true sends httpOnly cookies (DUALON_ACCESS, DUALON_REFRESH)
// - X-Client-Type: 'WEB' instructs Spring Security to use cookie-based auth
// - In-memory CSRF token synchronization with automatic retry on SEC_0006
// - Concurrency-safe Singleton Refresh Mutex preventing racing CTR rotations
import axios, { type InternalAxiosRequestConfig } from 'axios'
import toast from '@/utils/toast'
import { ENV } from '@/config/env'

// ── In-Memory & Session CSRF Token Store ────────────────────────────────────
const CSRF_STORAGE_KEY = 'dualon_csrf_token'

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'))
  return match && match[3] ? decodeURIComponent(match[3]) : null
}

function getStoredCsrfToken(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const fromCookie = getCookie('XSRF-TOKEN') || getCookie('X-XSRF-TOKEN')
    if (fromCookie) return fromCookie
    return sessionStorage.getItem(CSRF_STORAGE_KEY)
  } catch {
    return null
  }
}

let cachedCsrfToken: string | null = getStoredCsrfToken()
let syncCsrfPromise: Promise<string> | null = null

export function setCsrfToken(token: string | null): void {
  cachedCsrfToken = token
  if (typeof window !== 'undefined') {
    try {
      if (token) {
        sessionStorage.setItem(CSRF_STORAGE_KEY, token)
      } else {
        sessionStorage.removeItem(CSRF_STORAGE_KEY)
      }
    } catch {
      // Ignore storage errors
    }
  }
}

export function getCsrfToken(): string | null {
  return cachedCsrfToken || getStoredCsrfToken()
}

/**
 * Synchronizes the active CSRF token with the backend (GET /api/v1/auth/csrf).
 * Stores the token in memory and sessionStorage for attaching to mutating requests.
 * Uses a singleton promise to avoid multiple simultaneous GET /auth/csrf calls.
 */
export async function syncCsrfToken(): Promise<string> {
  if (syncCsrfPromise) {
    return syncCsrfPromise
  }

  syncCsrfPromise = (async () => {
    try {
      const url = ENV.API_URL.endsWith('/api/v1')
        ? `${ENV.API_URL}/auth/csrf`
        : `${ENV.API_URL}/api/v1/auth/csrf`

      const response = await axios.get(url, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-Client-Type': 'WEB',
          'ngrok-skip-browser-warning': 'true',
        },
      })
      const token =
        response.data?.data?.token ||
        response.headers?.['x-xsrf-token'] ||
        response.headers?.['x-csrf-token']

      if (token) {
        setCsrfToken(token)
        return token
      }
    } catch (err) {
      console.warn('[AUTH] Failed to synchronize CSRF token:', err)
    } finally {
      syncCsrfPromise = null
    }
    return ''
  })()

  return syncCsrfPromise
}

// ── Axios Instance ───────────────────────────────────────────────────────────
const axiosInstance = axios.create({
  baseURL: ENV.API_URL,
  timeout: 15000,
  withCredentials: true, // MANDATORY: Transmits HttpOnly cookies across origins
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Type': 'WEB', // MANDATORY: Identifies web channel to Spring Security
    'ngrok-skip-browser-warning': 'true',
  },
})

// ── Request Interceptor ──────────────────────────────────────────────────────
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Ensure URL has exactly one /api/v1 prefix relative to baseURL
    if (config.url && !config.url.startsWith('http://') && !config.url.startsWith('https://')) {
      const baseHasApiV1 = config.baseURL?.endsWith('/api/v1')
      const urlHasApiV1 = config.url.startsWith('/api/v1')

      if (baseHasApiV1 && urlHasApiV1) {
        config.url = config.url.replace(/^\/api\/v1/, '')
      } else if (!baseHasApiV1 && !urlHasApiV1) {
        config.url = config.url.startsWith('/') ? `/api/v1${config.url}` : `/api/v1/${config.url}`
      }
    }

    // Ensure X-Client-Type and ngrok header are always present
    if (config.headers.set) {
      config.headers.set('X-Client-Type', 'WEB')
      config.headers.set('ngrok-skip-browser-warning', 'true')
    } else {
      config.headers['X-Client-Type'] = 'WEB'
      config.headers['ngrok-skip-browser-warning'] = 'true'
    }

    // If uploading FormData, delete Content-Type so browser computes multipart boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type']
    }

    // Attach CSRF token on mutating HTTP methods
    const method = config.method?.toUpperCase()
    if (method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      let activeCsrf = cachedCsrfToken || getStoredCsrfToken()
      if (!activeCsrf) {
        // Proactively synchronize CSRF token so the initial mutation never fails with SEC_0006
        activeCsrf = await syncCsrfToken()
      }
      if (activeCsrf) {
        if (config.headers.set) {
          config.headers.set('X-XSRF-TOKEN', activeCsrf)
          config.headers.set('X-CSRF-TOKEN', activeCsrf)
        } else {
          config.headers['X-XSRF-TOKEN'] = activeCsrf
          config.headers['X-CSRF-TOKEN'] = activeCsrf
        }
      }
    }

    return config
  },
  (error) => Promise.reject(error)
)

// ── Response Interceptor (Singleton Mutex & CSRF Capture) ────────────────────
let refreshPromise: Promise<void> | null = null

axiosInstance.interceptors.response.use(
  (response) => {
    // Capture CSRF token if backend exposed it in response headers
    const exposedCsrf =
      response.headers?.['x-xsrf-token'] ||
      response.headers?.['x-csrf-token']
    if (exposedCsrf) {
      setCsrfToken(exposedCsrf)
    }
    return response
  },
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error)

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    const status = error.response?.status

    // Capture CSRF token from error responses if available
    if (error.response?.headers) {
      const errorCsrf =
        error.response.headers['x-xsrf-token'] ||
        error.response.headers['x-csrf-token']
      if (errorCsrf) setCsrfToken(errorCsrf)
    }

    // 1. Intercept 403 CSRF Failure (SEC_0006) -> Re-sync token and retry mutation once
    const errorCode =
      (error.response?.data as any)?.error?.code ||
      (error.response?.data as any)?.code
    if (status === 403 && errorCode === 'SEC_0006' && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true
      const freshToken = (await syncCsrfToken()) || cachedCsrfToken
      if (freshToken) {
        if (originalRequest.headers.set) {
          originalRequest.headers.set('X-XSRF-TOKEN', freshToken)
          originalRequest.headers.set('X-CSRF-TOKEN', freshToken)
        } else {
          originalRequest.headers['X-XSRF-TOKEN'] = freshToken
          originalRequest.headers['X-CSRF-TOKEN'] = freshToken
        }
        return axiosInstance(originalRequest)
      }
    }

    // 2. Intercept 401 Unauthorized -> Concurrency-safe session rotation via Singleton Mutex
    if (status === 401 && originalRequest && !originalRequest._retry) {
      const url = originalRequest.url || ''
      const isAuthOrPublicEndpoint =
        url.includes('/auth/refresh') ||
        url.includes('/auth/login') ||
        url.includes('/auth/forgot-password') ||
        url.includes('/certificates/verify') ||
        url.includes('/institutions/codes') ||
        url.includes('/syllabus/standards')

      if (isAuthOrPublicEndpoint) {
        return Promise.reject(error)
      }

      originalRequest._retry = true

      // Singleton Mutex: Exactly one refresh request in-flight across all concurrent callers
      if (!refreshPromise) {
        refreshPromise = (async () => {
          try {
            const refreshUrl = ENV.API_URL.endsWith('/api/v1')
              ? `${ENV.API_URL}/auth/refresh`
              : `${ENV.API_URL}/api/v1/auth/refresh`

            const response = await axios.post(
              refreshUrl,
              {},
              {
                withCredentials: true,
                headers: {
                  'Content-Type': 'application/json',
                  'X-Client-Type': 'WEB',
                  'ngrok-skip-browser-warning': 'true',
                },
              }
            )

            if (response.data?.success && response.data?.data) {
              const { store } = await import('@/app/store')
              const { setUser } = await import('@/features/auth/auth.slice')
              store.dispatch(setUser(response.data.data))
            }
          } finally {
            refreshPromise = null
          }
        })()
      }

      try {
        await refreshPromise
        // Re-execute original request with newly rotated cookies
        return axiosInstance(originalRequest)
      } catch (refreshErr) {
        // Refresh token expired or revoked -> session is dead
        setCsrfToken(null)
        window.dispatchEvent(new Event('auth:session-expired'))

        const { store } = await import('@/app/store')
        const { clearUser } = await import('@/features/auth/auth.slice')
        store.dispatch(clearUser())

        const path = window.location.pathname
        const isPublicRoute =
          path === '/login' ||
          path === '/login/' ||
          path === '/register' ||
          path === '/register/' ||
          path.startsWith('/register') ||
          path === '/forgot-password' ||
          path === '/forgot-password/' ||
          path.startsWith('/forgot-password') ||
          path === '/unauthorized' ||
          path.startsWith('/verify-certificate') ||
          path.startsWith('/certificates/verify')

        if (!isPublicRoute) {
          window.location.replace('/login')
        }

        return Promise.reject(refreshErr)
      }
    }

    // 3. Genuine 403 Forbidden (Permission Denial SEC_0002 or unrecoverable)
    if (status === 403 && errorCode !== 'SEC_0006') {
      const message =
        (error.response?.data as any)?.error?.message ||
        (error.response?.data as any)?.message ||
        'You do not have permission to access this resource.'
      toast.error(message)
    }

    if (status === 500) {
      toast.error('Something went wrong. Please try again.')
    }

    return Promise.reject(error)
  }
)

export const apiClient = axiosInstance
export default axiosInstance
